import React, { useEffect, useState, useCallback } from 'react';
import styles from './StudentTrendChart.module.scss';
import Chart from 'react-google-charts';
import Dropdown from '../dropdown/Dropdown';
import Filter from '../filter/Filter';
import LoadingOverlay from '../loadingOverlay/LoadingOverlay';
import useDeviceType from '../../hooks/useDeviceType';
import { useToasts } from '../../contexts/ToastContext';
import axiosClient from '../../api/axiosClient';
import PropTypes from 'prop-types';

const TREND_TYPES = [
  { label: 'Competency', value: 'competencyTrends' },
  { label: 'Anesthesia', value: 'anesthesiaTrends' },
  { label: 'Questions', value: 'questionTrends' },
];

const formatPeriod = (period) => {
  const [year, month] = period.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleString('default', { month: 'short' }) + " '" + year.slice(2);
};

const buildChartData = (dataPoints) => {
  const rows = dataPoints
    .filter(p => p.studentScore !== null || p.cohortScore !== null)
    .map(p => [formatPeriod(p.period), p.studentScore ?? null, p.cohortScore ?? null]);
  return [['Period', 'Student Score', 'Cohort Score'], ...rows];
};

const StudentTrendChart = ({ userId }) => {
  const { showToast } = useToasts();
  const { isMobile } = useDeviceType();
  const [isLoading, setIsLoading] = useState(false);
  const [trendData, setTrendData] = useState(null);
  const [trendType, setTrendType] = useState('competencyTrends');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const fetchTrends = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (userId) params.userId = userId;
      const response = await axiosClient.get('/reports/trend-lines', { params });
      const trends = response.data;
      setTrendData(trends);
      const first = trends?.competencyTrends?.[0];
      if (first) setSelectedCategory(first.category || first.question || null);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [userId, showToast]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  useEffect(() => {
    if (!trendData) return;
    const list = trendData[trendType] || [];
    const first = list[0];
    setSelectedCategory(first ? (first.category || first.question || null) : null);
  }, [trendType, trendData]);

  const getCategoryOptions = () => {
    if (!trendData) return [];
    return (trendData[trendType] || []).map(item => ({
      value: item.category || item.question,
      label: item.category || item.question,
    }));
  };

  const getChartData = () => {
    if (!trendData || !selectedCategory) return null;
    const item = (trendData[trendType] || []).find(i => (i.category || i.question) === selectedCategory);
    if (!item?.dataPoints?.length) return null;
    return buildChartData(item.dataPoints);
  };

  const chartData = getChartData();
  const categoryOptions = getCategoryOptions();

  const chartOptions = {
    vAxis: {
      title: 'Average Score',
      viewWindow: { min: 0, max: 6 },
      ticks: [0, 1, 2, 3, 4, 5],
      gridlines: { color: 'none' },
      textStyle: { fontSize: 12 },
      baselineColor: '#BFBFBF',
      titleTextStyle: { fontSize: 14, italic: false },
    },
    hAxis: {
      title: isMobile ? 'Period' : '',
      textStyle: { fontSize: 12 },
      textPosition: isMobile ? 'none' : 'out',
      titleTextStyle: { fontSize: 14, italic: false },
    },
    legend: { position: 'top' },
    chartArea: {
      left: '10%', top: '10%', right: '5%', bottom: '10%',
      backgroundColor: { stroke: '#BFBFBF', strokeWidth: 0.5 },
    },
    series: {
      0: {
        type: 'area',
        areaOpacity: 0.1,
        color: '#171898',
        lineWidth: 1,
        pointSize: 6,
        pointShape: 'circle',
        labelInLegend: 'Student Score',
      },
      1: {
        type: 'line',
        color: '#E04B4B',
        lineWidth: 1.5,
        lineDashStyle: [6, 4],
        pointSize: 4,
        labelInLegend: 'Cohort Score',
      },
    },
  };

  return (
    <div className={styles.container}>
      {isLoading && <LoadingOverlay />}
      <div className={styles.header}>
        <h3 className={styles.title}>Trends</h3>
        {categoryOptions.length > 0 && (
          <div className={styles.categoryDropdown}>
            <Dropdown
              options={categoryOptions}
              value={selectedCategory}
              onChange={setSelectedCategory}
            />
          </div>
        )}
      </div>
      <div className={styles.typeFilter}>
        <Filter
          filters={TREND_TYPES}
          selected={trendType}
          onChange={setTrendType}
        />
      </div>
      <div className={`${styles.chartArea} ${isMobile ? styles.chartAreaMobile : ''}`}>
        {chartData ? (
          <Chart
            chartType="ComboChart"
            width="100%"
            height="100%"
            data={chartData}
            options={chartOptions}
          />
        ) : (
          !isLoading && <p className={styles.noData}>No trend data available.</p>
        )}
      </div>
    </div>
  );
};

StudentTrendChart.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default StudentTrendChart;
